import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Circle, Download, Upload } from "lucide-react";

import { timetableService, type TimetableGeneration, type TimetablePeriod, type TimetableSlot } from "../../services/timetableService";
import { apiErrorMessage } from "../../utils/apiError";
import { Field } from "../FormLayout";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { generationChecklist, generationConfig, generationStatus, isLesson, type Assignment } from "./timetableLogic";

const when = (value?: string | null) => (value ? value.slice(0, 16).replace("T", " ") : "");

const generationColumns: RegisterColumn<TimetableGeneration>[] = [
  { header: "Timetable", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "min-w-[12rem] whitespace-normal" },
  { header: "Generated", cell: (row) => when(row.created_at), exportValue: (row) => when(row.created_at), className: "whitespace-nowrap tabular-nums text-gray-700" },
  { header: "By", cell: (row) => row.generated_by_name, exportValue: (row) => row.generated_by_name, className: "text-gray-700" },
  {
    header: "Clashes",
    cell: (row) => (row.conflicts_found ? <span className="font-medium text-red-700">{row.conflicts_found}</span> : 0),
    exportValue: (row) => row.conflicts_found,
    align: "right",
  },
  {
    header: "Warnings",
    cell: (row) => (row.warnings_found ? <span className="text-amber-700">{row.warnings_found}</span> : 0),
    exportValue: (row) => row.warnings_found,
    align: "right",
  },
  { header: "Status", cell: (row) => <StatusDot {...generationStatus(row)} />, exportValue: (row) => generationStatus(row).label },
];

/**
 * Making the timetable: what must be in place, the generator, what earlier
 * runs produced, and the CSV route for timetables made elsewhere.
 */
export function GeneratePanel({ yearId, yearName, periods, assignments, slots, generations, isLoading, canWrite, onChanged }: {
  yearId: number | null;
  yearName: string;
  periods: TimetablePeriod[];
  assignments: Assignment[];
  slots: TimetableSlot[];
  generations: TimetableGeneration[] | undefined;
  isLoading: boolean;
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [importReport, setImportReport] = useState<{ errors: string[]; warnings: string[] } | null>(null);
  const yearGenerations = generations?.filter((item) => item.academic_year === yearId);
  const active = yearGenerations?.find((item) => item.is_active);
  const checklist = generationChecklist(periods, assignments, slots);
  const ready = checklist[0].done && checklist[1].done;

  const generate = useMutation({
    mutationFn: timetableService.generateTimetable,
    onSuccess: (result) => {
      setNotice({
        tone: result.conflicts_found ? "error" : "success",
        text: result.conflicts_found
          ? `Generated with ${result.conflicts_found} clashes it could not resolve. Open it below to see the log.`
          : "Timetable generated, put in use and added to the school-year planner.",
      });
      onChanged();
    },
    onError: (error) => setNotice({ tone: "error", text: apiErrorMessage(error, "The timetable could not be generated.") }),
  });
  const importFile = useMutation({
    mutationFn: ({ file, overwrite, validateOnly }: { file: File; overwrite: boolean; validateOnly: boolean }) =>
      timetableService.importTimetable(file, { academic_year_id: yearId!, overwrite_existing: overwrite, validate_only: validateOnly }),
    onSuccess: (result, variables) => {
      setImportReport(result.errors.length || result.warnings.length ? { errors: result.errors, warnings: result.warnings } : null);
      setNotice({
        tone: result.errors.length ? "error" : "success",
        text: variables.validateOnly
          ? `${result.validated_count} ${result.validated_count === 1 ? "row checks" : "rows check"} out${result.errors.length ? `; ${result.errors.length} ${result.errors.length === 1 ? "problem" : "problems"} below` : ". Nothing was imported"}.`
          : `${result.imported_count} imported, ${result.updated_count} updated.`,
      });
      if (!variables.validateOnly) onChanged();
    },
    onError: (error) => setNotice({ tone: "error", text: apiErrorMessage(error, "The file could not be processed.") }),
  });

  const submitGeneration = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    if (active && data.get("force") !== "on") {
      setNotice({ tone: "error", text: `${active.name} is in use. Tick "Replace the timetable in use" to generate a new one.` });
      return;
    }
    generate.mutate({ name: String(data.get("name")).trim(), academic_year_id: yearId!, force_regenerate: data.get("force") === "on" });
  };
  const submitImport = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const file = data.get("file");
    if (!(file instanceof File) || !file.size) return;
    importFile.mutate(
      { file, overwrite: data.get("overwrite") === "on", validateOnly: data.get("validate_only") === "on" },
      { onSuccess: () => form.reset() },
    );
  };
  const exportCsv = async () => {
    try {
      const blob = await timetableService.exportTimetable({ academic_year_id: yearId! });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${yearName || "school"}-timetable.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setNotice({ tone: "error", text: apiErrorMessage(error, "The CSV could not be downloaded.") });
    }
  };

  const generation = viewing !== null ? generations?.find((item) => item.id === viewing) : undefined;

  return (
    <div className="space-y-6">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      {canWrite && (
        <form onSubmit={submitGeneration} className="card">
          <div className="card-titlebar">
            <div>
              <h3 className="font-semibold text-gray-900">Generate the timetable</h3>
              <p className="mt-1 text-sm text-gray-500">No class or teacher is booked twice. The result is put in use and added to the school-year planner.</p>
            </div>
          </div>
          <div className="card-content space-y-4 pt-5">
            <ol className="grid gap-2 sm:grid-cols-3">
              {checklist.map((step, index) => (
                <li key={step.label} className="flex items-start gap-2 text-sm">
                  {step.done ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />}
                  <span className={step.done ? "text-gray-500" : "text-gray-900"}>{index + 1}. {step.label}</span>
                </li>
              ))}
            </ol>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name">
                <input required name="name" key={yearName} defaultValue={`${yearName} Teaching Timetable`} className="input mt-1" />
              </Field>
              {active && (
                <label className="flex cursor-pointer items-start gap-2.5 self-end rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm has-[:checked]:border-amber-500">
                  <input type="checkbox" name="force" className="mt-0.5 h-4 w-4 accent-amber-600" />
                  <span>
                    <span className="block font-medium text-gray-900">Replace the timetable in use</span>
                    <span className="block text-xs text-gray-600">{active.name} and any hand-made changes to it are replaced.</span>
                  </span>
                </label>
              )}
            </div>
          </div>
          <div className="card-footer">
            <p className="mr-auto text-xs text-gray-500">{ready ? `${periods.filter(isLesson).length} lesson periods, ${assignments.length} teaching assignments.` : "Finish steps 1 and 2 first."}</p>
            <button disabled={!yearId || !ready || generate.isPending} className="btn btn-primary">{generate.isPending ? "Generating…" : "Generate timetable"}</button>
          </div>
        </form>
      )}

      <ClientRegister
        title={`Generated timetables · ${yearName}`}
        description="Every run of the generator for this year, and which one is in use."
        rows={yearGenerations}
        config={generationConfig}
        columns={generationColumns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewing(row.id)}
        noun={["timetable", "timetables"]}
        exportName={{ file: "timetable-generations", title: `Generated Timetables ${yearName}` }}
        isLoading={isLoading}
        emptyText="The generator has not been run for this year."
      />

      {canWrite && (
        <div className="grid gap-5 lg:grid-cols-2">
          <section className="card">
            <div className="card-titlebar">
              <div>
                <h3 className="font-semibold text-gray-900">Download for editing</h3>
                <p className="mt-1 text-sm text-gray-500">A CSV with year, subject-code and teacher-username columns. Edit it and import it back below.</p>
              </div>
            </div>
            <div className="card-footer">
              <button type="button" disabled={!yearId} onClick={exportCsv} className="btn btn-outline gap-1.5"><Download className="h-4 w-4" /> Download CSV</button>
            </div>
          </section>
          <form onSubmit={submitImport} className="card">
            <div className="card-titlebar">
              <div>
                <h3 className="font-semibold text-gray-900">Import a CSV</h3>
                <p className="mt-1 text-sm text-gray-500">Check a file first; nothing is saved until every row is valid.</p>
              </div>
            </div>
            <div className="card-content space-y-3 pt-5">
              <Field label="File">
                <input required type="file" accept=".csv,text/csv" name="file" className="input mt-1" />
              </Field>
              <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                <label className="flex items-center gap-2"><input type="checkbox" name="validate_only" defaultChecked className="h-4 w-4 accent-primary-600" /> Only check it</label>
                <label className="flex items-center gap-2"><input type="checkbox" name="overwrite" className="h-4 w-4 accent-primary-600" /> Replace lessons already in those class slots</label>
              </div>
              {importReport && (
                <div className="space-y-2 text-sm">
                  {importReport.errors.length > 0 && (
                    <ul className="list-disc space-y-1 rounded-md bg-red-50 py-2 pl-8 pr-3 text-red-800">{importReport.errors.map((line) => <li key={line}>{line}</li>)}</ul>
                  )}
                  {importReport.warnings.length > 0 && (
                    <ul className="list-disc space-y-1 rounded-md bg-amber-50 py-2 pl-8 pr-3 text-amber-900">{importReport.warnings.map((line) => <li key={line}>{line}</li>)}</ul>
                  )}
                </div>
              )}
            </div>
            <div className="card-footer">
              <button disabled={!yearId || importFile.isPending} className="btn btn-outline gap-1.5"><Upload className="h-4 w-4" /> {importFile.isPending ? "Processing…" : "Process file"}</button>
            </div>
          </form>
        </div>
      )}

      {generation && (
        <DetailsModal
          title={generation.name}
          headline={{ label: "Generated", value: when(generation.created_at), note: generation.generated_by_name ? `by ${generation.generated_by_name}` : undefined }}
          status={generationStatus(generation)}
          facts={[
            ["Academic year", generation.academic_year_name],
            ["Clashes left", String(generation.conflicts_found)],
            ["Warnings", String(generation.warnings_found)],
            ["Template", generation.template_name || "None"],
            ["Time taken", generation.generation_time || undefined],
          ]}
          sections={[{ title: "What the generator did", body: generation.generation_log }]}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}
